import { Box, Skeleton } from "@mui/material";
import { type FC, type KeyboardEvent, useRef } from "react";
import { CONTROL_HEIGHT } from "../../theme/tokens";
import { PrintoutPresetRow } from "./PrintoutPresetRow";
import type { PrintoutPreset } from "./printoutPresets";

/** Test-id key for Printout 1's radio, which has no preset id. */
const PRINTOUT_1_TEST_ID_KEY = "first";

/** Arrow keys a radio group answers, and which way each moves (WAI-ARIA radio group pattern). */
const ARROW_STEP: Record<string, 1 | -1> = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };

type Props = {
	/** Id of the visible label naming the group. */
	labelId: string;
	printout1Label: string;
	presets: PrintoutPreset[];
	/** `null` = Printout 1. */
	activePresetId: string | null;
	/** Saved printouts still loading: a placeholder row stands in for them. */
	loading: boolean;
	/** Switching is on hold (Annie is changing the printout on screen). */
	disabled: boolean;
	onSelect: (presetId: string | null) => void;
};

/**
 * The printout picker: a `role="radiogroup"` of Printout 1 and the saved printouts. Tab lands on the
 * chosen one (roving tabindex); the arrow keys move to the next or previous printout and pick it, wrapping
 * at the ends, and Home/End jump to the first/last — the radio-group keyboard contract `RoundedRadioBox`
 * is drawn for.
 */
export const PrintoutPresetPicker: FC<Props> = ({ labelId, printout1Label, presets, activePresetId, loading, disabled, onSelect }) => {
	const options: { id: string | null; name: string; testIdKey: string }[] = [
		{ id: null, name: printout1Label, testIdKey: PRINTOUT_1_TEST_ID_KEY },
		...presets.map((preset) => ({ id: preset.id, name: preset.name, testIdKey: preset.id })),
	];
	const radioRefs = useRef(new Map<string, HTMLDivElement>());

	/** Where a key moves within the group, or `null` for a key the group doesn't handle. */
	const targetIndexFor = (key: string): number | null => {
		if (key === "Home") return 0;
		if (key === "End") return options.length - 1;
		const step = ARROW_STEP[key];
		if (step === undefined) return null;
		const from = options.findIndex((option) => option.id === activePresetId);
		return (from + step + options.length) % options.length;
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
		const targetIndex = targetIndexFor(event.key);
		if (targetIndex === null) return;
		event.preventDefault();
		if (disabled) return;
		const target = options[targetIndex];
		if (target.id !== activePresetId) onSelect(target.id);
		radioRefs.current.get(target.testIdKey)?.focus();
	};

	return (
		<Box role="radiogroup" aria-labelledby={labelId} onKeyDown={handleKeyDown} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
			{options.map((option) => (
				<PrintoutPresetRow
					key={option.testIdKey}
					ref={(element) => {
						if (element) radioRefs.current.set(option.testIdKey, element);
						else radioRefs.current.delete(option.testIdKey);
					}}
					name={option.name}
					checked={option.id === activePresetId}
					tabbable={option.id === activePresetId}
					disabled={disabled}
					onSelect={() => onSelect(option.id)}
					testIdKey={option.testIdKey}
				/>
			))}
			{loading && <Skeleton variant="rounded" height={CONTROL_HEIGHT.COMPACT} aria-hidden data-testid="printout-preset-loading" />}
		</Box>
	);
};
